/**
 * SOLID Review
 * S — Good. One concern: the machinery every session decision shares — the draft of table and session,
 *     settling it into changes, seating a player, and the three tables a seat can be built from.
 * O — Good. A new seat source is one more builder; a new session field needs nothing here.
 * L — N/A — functions.
 * I — Good. Each export is one step a session decision takes.
 * D — Strong. The table's own draft machinery, the roster rule and the schemas' types.
 */
import { GamePhase } from '../../uml-components/primitives';
import { settle, type IConfigSettle } from '../table/tableDraft';
import { deriveTable } from '../table/deriveTable';
import { buildRosterFromRaw } from '../../game/Roster';
import { sameJson } from '../../../shared/sameJson';
import type { IStateClientTable, IEntityClientRoom } from '../../ports/schemas/ClientTable';
import type { IStateClientSession, IPropsClientSessionChanges, IEntityClientPlayer, IEntityClientResume, IEntityClientGuest } from '../../ports/schemas/ClientSession';
import type { IPropsClientChanges, IPropsClientInput } from '../../ports/schemas/ClientChanges';
import type { IPayloadSeatRoom } from '../../ports/schemas/SessionMoves';

/**
 * HOW A SESSION HOOK BECAME A DECISION (frontend strict-ports plan, FE2, 2026-10-03). As FE1's handlers:
 * each decision replays its hook's writes on a DRAFT — the table and the session, copied — and
 * `settleSession` reports what differs. The table half settles exactly as a table decision does, so a
 * seat taken here is remembered by the same rule (`settle`'s stored-game rule); the session half reports
 * every field that changed, each whole.
 *
 * One identity, two views: the table's `localPlayerId` and the session's `player` are the same store
 * field (`localUser`). A draft changes them together, through `withPlayer`.
 */
export interface IStateClientDraft {
  table: IStateClientTable;
  session: IStateClientSession;
}

export function draftOf(input: IPropsClientInput): IStateClientDraft {
  return { table: { ...input.table }, session: { ...input.session } };
}

type TSessionField = keyof IStateClientSession;
const SESSION_FIELDS: ReadonlyArray<TSessionField> = [
  'account', 'player', 'screen', 'pendingRoomCode', 'hostOnEntry', 'pendingJoinError', 'pendingEmail', 'consent', 'ads',
  'media', 'restoring', 'entering', 'hosting', 'hostError',
];

function assign<K extends TSessionField>(changes: IPropsClientSessionChanges, key: K, value: IStateClientSession[K]): void {
  changes[key] = value;
}

export interface IConfigSettleSession extends IConfigSettle {
  /** A guest identity to keep across a reload, or null to forget the stored one (FE3). */
  readonly guest?: IEntityClientGuest | null;
  /** The stored game to keep (a record) or forget (null), whatever the seat rule says. */
  readonly resume?: IEntityClientResume | null;
}

/**
 * The changes that turn `base` into `next`: the table's (with `settle`'s stored-game rule), the derived
 * values when only the local player changed — `settle` does not see identity, and the derived values
 * hang on it — the session fields that differ, and anything else to keep.
 */
export function settleSession(base: IPropsClientInput, next: IStateClientDraft, options: IConfigSettleSession = {}): IPropsClientChanges {
  const how: IConfigSettle = { markActivity: options.markActivity, reset: options.reset };
  const changes = settle(base.table, next.table, how);
  if (changes.derived === undefined && base.table.localPlayerId !== next.table.localPlayerId) changes.derived = deriveTable(next.table);
  const session: IPropsClientSessionChanges = {};
  for (const key of SESSION_FIELDS) {
    if (!sameJson(base.session[key], next.session[key])) assign(session, key, next.session[key]);
  }
  if (Object.keys(session).length > 0) changes.session = session;
  if (options.guest !== undefined) changes.persist = { ...changes.persist, guest: options.guest };
  if (options.resume !== undefined) changes.persist = { ...changes.persist, resume: options.resume };
  return changes;
}

/** Seat the tab as `player`: the session's player and the table's local id move together. */
export function withPlayer(next: IStateClientDraft, player: IEntityClientPlayer): void {
  next.session.player = player;
  next.table.localPlayerId = player.playerId;
}

/** Every table seats two to six (the backend's rule; the client only displays it). */
const MIN_PLAYERS = 2;
const MAX_PLAYERS = 6;

/** A room as `joinRoom`/`lateJoinRoom` answer it, or a rejoin's — the old `buildRoomEntity`. */
export interface IEntityRoomSource {
  readonly id: string;
  readonly roomCode: string;
  readonly playerIds?: ReadonlyArray<string>;
  readonly players: ReadonlyArray<{ readonly id: string; readonly displayName: string }>;
  readonly status: string;
}

/**
 * A table joined by code, seen from `me` (application/mappers/buildRoomEntity.ts, moved). The host is
 * the first listed seat, or this player when none is listed.
 */
export function joinedRoom(raw: IEntityRoomSource, me: string): IEntityClientRoom {
  return {
    roomId: raw.id,
    roomCode: raw.roomCode,
    hostPlayerId: (raw.playerIds ?? raw.players.map((p) => p.id))[0] ?? me,
    isGameActive: raw.status === 'IN_PROGRESS',
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    players: buildRosterFromRaw(raw.players, me),
  };
}

/**
 * A stored game's table, re-joined (useGameStateRestore's room). The record fills what the answer lacks:
 * its code, and the host when no seat is listed.
 */
export function restoredRoom(raw: IPayloadSeatRoom, record: IEntityClientResume): IEntityClientRoom {
  return {
    roomId: raw.id,
    hostPlayerId: raw.playerIds[0] ?? record.playerId,
    roomCode: raw.roomCode || record.roomCode,
    isGameActive: raw.status === 'IN_PROGRESS',
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    players: buildRosterFromRaw(raw.players, record.playerId),
  };
}

/**
 * A table this player just created (MainMenuPanel's room): theirs to host, not yet started. The code is
 * the server's; a missing one is left blank, never derived from the id — a derived code looks real and
 * joins nothing.
 */
export function hostedRoom(raw: IPayloadSeatRoom, host: string): IEntityClientRoom {
  return {
    roomId: raw.id,
    roomCode: raw.roomCode,
    hostPlayerId: host,
    maxPlayers: MAX_PLAYERS,
    minPlayers: MIN_PLAYERS,
    isGameActive: false,
    players: buildRosterFromRaw(raw.players, host),
  };
}

/** A seat at a table that has not started: the lobby. */
export function inLobby(next: IStateClientDraft): void {
  next.table.phase = GamePhase.LOBBY;
}

/** The welcome screen's banner for a join that did not happen; the code that led to it is dropped. */
export function joinFailed(next: IStateClientDraft, message: string): void {
  next.session.pendingJoinError = message;
  next.session.pendingRoomCode = null;
}

/**
 * A seat was taken: whatever code was pending is spent, and an old join error is irrelevant (AppRoot
 * cleared it when a room appeared).
 */
export function seated(next: IStateClientDraft, room: IEntityClientRoom): void {
  next.table.room = room;
  next.session.pendingRoomCode = null;
  next.session.pendingJoinError = null;
}

/**
 * A returning guest, seated from the identity this tab already holds (useIdentityBootstrap's guest effect):
 * free tier, no credits. Null unless both the id and the name are there.
 */
export function guestPlayer(session: IStateClientSession): IEntityClientPlayer | null {
  const { playerId, displayName } = session.account;
  if (!playerId || !displayName) return null;
  return { playerId, displayName, matchCredits: 0, isAdmin: false };
}
